import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { Toast, ConfirmationModal } from '../components/ui/Notification';
import { 
  FiArrowLeft, FiSave, FiCopy, FiCheck, FiSend, FiFileText, 
  FiGlobe, FiCalendar, FiMail, FiMessageCircle, FiPhone, 
  FiLock, FiLoader, FiAlertCircle, FiInfo, FiTrash2, FiExternalLink 
} from 'react-icons/fi';

export default function AdminClientDetail() {
  const { clientKey } = useParams();
  const navigate = useNavigate();
  const { adminUser } = useAuth();
  
  const [loading, setLoading] = useState(!!clientKey);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Staged Test Lead Logic
  const [testStage, setTestStage] = useState(0); // 0: idle, 1-5: stages
  const [testResult, setTestResult] = useState(null);
  
  // Modal State
  const [modal, setModal] = useState({ open: false, type: null });
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);

  // Helper to ensure dates are in yyyy-MM-dd format for HTML inputs
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    // Extract yyyy-MM-dd from ISO strings or return as is if already formatted
    return dateStr.split('T')[0];
  };

  const [formData, setFormData] = useState({
    client_key: '',
    client_name: '',
    business_name: '',
    website_domain: '',
    allowed_domains: '',
    status: 'Trial',
    plan: 'Standard',
    notes: '',
    service_start_date: formatDateForInput(new Date().toISOString()),
    service_end_date: '',
    grace_period_days: 7,
    disable_notifications_after_end_date: true,
    send_business_notification: true,
    business_notification_email: '',
    cc_email: '',
    reply_to_email: '',
    send_customer_confirmation: false,
    customer_ack_from_name: '',
    customer_confirmation_subject: 'Thank you for your enquiry',
    send_sms_alert: false,
    sms_mobile: '',
    client_portal_enabled: true,
    client_login_emails: '',
    client_login_code: '',
    client_portal_role: 'Client Lead',
    sheet_id: '',
    sheet_url: '',
    client_sheet_auto_write: true
  });

  const SCRIPT_BASE = import.meta.env.VITE_BPAS_PUBLIC_SCRIPT_BASE_URL;

  const scriptInfo = useMemo(() => {
    const key = formData.client_key || clientKey;
    const isSaved = !!key;
    return {
      code: isSaved && SCRIPT_BASE ? `<script async src="${SCRIPT_BASE}?k=${key}"></script>` : null,
      isSaved
    };
  }, [formData.client_key, clientKey, SCRIPT_BASE]);

  useEffect(() => {
    if (clientKey) {
      const fetchClient = async () => {
        try {
          const res = await adminApi.getClient(adminUser.admin_session_token, clientKey);
          if (res.success && res.client) {
            setFormData(prev => ({ 
              ...prev, 
              ...res.client,
              // Ensure dates are formatted correctly for input[type="date"]
              service_start_date: formatDateForInput(res.client.service_start_date),
              service_end_date: formatDateForInput(res.client.service_end_date)
            }));
          }
        } catch (e) {
          setNotification({ message: 'Failed to load client data.', type: 'error' });
        } finally {
          setLoading(false);
        }
      };
      fetchClient();
    }
  }, [clientKey, adminUser]);

  const validateForm = () => {
    const errors = {
      profile: [],
      notifications: [],
      portal: []
    };

    if (!formData.client_name) errors.profile.push("Client Full Name");
    if (!formData.business_name) errors.profile.push("Business Identity");
    if (!formData.website_domain) errors.profile.push("Primary Domain");

    if (formData.send_business_notification && !formData.business_notification_email) {
      errors.notifications.push("Recipient Email (Business Alerts)");
    }
    if (formData.send_customer_confirmation && !formData.reply_to_email) {
      errors.notifications.push("Reply-to Address (Customer Ack)");
    }

    if (formData.client_portal_enabled) {
      if (!formData.client_login_emails) errors.portal.push("Portal Login Whitelist");
      if (!formData.client_login_code || formData.client_login_code.length !== 6) {
        errors.portal.push("Client Portal Passcode (must be exactly 6 digits)");
      }
    }

    const hasErrors = errors.profile.length > 0 || errors.notifications.length > 0 || errors.portal.length > 0;
    setValidationErrors(hasErrors ? errors : {});
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    try {
      const res = clientKey 
        ? await adminApi.updateClient(adminUser.admin_session_token, { ...formData, client_key: clientKey })
        : await adminApi.addClient(adminUser.admin_session_token, formData);

      if (res.success) {
        const updatedKey = res.client?.client_key || res.client_key;
        if (updatedKey) setFormData(prev => ({ ...prev, client_key: updatedKey }));
        
        setNotification({ message: 'Client configuration saved.', type: 'success' });
        if (updatedKey) setNotification({ message: 'Install script is ready.', type: 'info' });

        if (!clientKey && updatedKey) {
          navigate(`/admin/clients/${updatedKey}`, { replace: true });
        }
      } else {
        setNotification({ message: res.message || 'Save failed.', type: 'error' });
      }
    } catch (e) {
      setNotification({ message: 'Gateway connection failed.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestLead = async () => {
    if (!clientKey) {
      setNotification({ message: 'Save the client first before sending a test lead.', type: 'error' });
      return;
    }

    setTestResult(null);
    setTestStage(1);
    
    try {
      setTimeout(() => setTestStage(2), 1000);
      const res = await adminApi.sendTestLead(adminUser.admin_session_token, clientKey);
      setTestStage(3);
      
      if (res.success) {
        setTestStage(4);
        setTestResult(res);
        setNotification({ message: 'Test lead created successfully.', type: 'success' });
        
        if (res.notification_status?.toLowerCase().includes('failed')) {
          setNotification({ 
            message: 'Lead created, but one or more notifications failed. Check Logs.', 
            type: 'error' 
          });
        }
      } else {
        setNotification({ message: res.message || 'Test lead failed.', type: 'error' });
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        setNotification({ 
          message: 'Test lead request timed out. Check Leads and Logs before retrying.', 
          type: 'info' 
        });
      } else {
        setNotification({ message: 'Test lead transmission failed.', type: 'error' });
      }
    } finally {
      setTestStage(5);
    }
  };

  const handleCreateSheet = async () => {
    setIsCreatingSheet(true);
    setNotification({ message: 'Creating client lead sheet...', type: 'info' });
    
    try {
      const res = await adminApi.createSheet(
        adminUser.admin_session_token, 
        clientKey, 
        formData.business_notification_email
      );
      
      if (res.success) {
        setFormData(prev => ({ ...prev, sheet_id: res.sheet_id, sheet_url: res.sheet_url }));
        setNotification({ message: 'Client lead sheet created.', type: 'success' });
      } else {
        setNotification({ message: res.message || 'Could not create sheet.', type: 'error' });
      }
    } catch (e) {
      setNotification({ message: 'Sheet creation failed.', type: 'error' });
    } finally {
      setIsCreatingSheet(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center p-20"><SafeIcon icon={FiLoader} className="animate-spin text-4xl text-brand-blue" /></div>;

  return (
    <div className="space-y-8 pb-20">
      {notification && <Toast {...notification} onClose={() => setNotification(null)} />}
      
      <ConfirmationModal 
        isOpen={modal.open && modal.type === 'createSheet'}
        title="Create client lead sheet?"
        message="This will create a separate Google Sheet for this client and can optionally be shared with the client email. Your master BPAS sheet will not be exposed."
        confirmText="Create Sheet"
        loading={isCreatingSheet}
        onConfirm={() => {
          setModal({ open: false, type: null });
          handleCreateSheet();
        }}
        onCancel={() => setModal({ open: false, type: null })}
      />

      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate('/admin/clients')} className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-slate-400 hover:text-navy-900 transition-colors border border-slate-100">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tighter">
              {clientKey ? (formData.business_name || 'Edit Client') : 'New Client'}
            </h1>
            <p className="text-slate-400 text-[10px] uppercase font-black tracking-widest mt-1 opacity-60">
              {clientKey ? `ID: ${clientKey}` : 'Provisioning Wizard'}
            </p>
          </div>
        </div>
        {clientKey && (
          <div className="flex items-center gap-3">
            <button 
              type="button" 
              onClick={handleSendTestLead}
              disabled={testStage > 0 && testStage < 5}
              className="px-5 py-3 bg-brand-cyan/10 text-brand-cyan rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-brand-cyan hover:text-white transition-all disabled:opacity-50"
            >
              <SafeIcon icon={testStage > 0 && testStage < 5 ? FiLoader : FiSend} className={testStage > 0 && testStage < 5 ? 'animate-spin' : ''} />
              {testStage > 0 && testStage < 5 ? 'Testing...' : 'Send Test Lead'}
            </button>
            <button 
              type="button" 
              onClick={() => setModal({ open: true, type: 'createSheet' })}
              disabled={!!formData.sheet_id || isCreatingSheet}
              className="px-5 py-3 bg-emerald-500/10 text-emerald-500 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-500 hover:text-white transition-all disabled:opacity-50"
            >
              <SafeIcon icon={isCreatingSheet ? FiLoader : FiFileText} className={isCreatingSheet ? 'animate-spin' : ''} />
              {formData.sheet_id ? 'Sheet Active' : 'Create Client Lead Sheet'}
            </button>
          </div>
        )}
      </header>

      {testStage > 0 && (
        <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center justify-between mb-6">
             <h4 className="font-black uppercase tracking-widest text-xs text-slate-900">Test Execution Diagnostic</h4>
             <span className={`px-2 py-1 rounded text-[9px] font-black uppercase ${testStage === 5 ? 'bg-emerald-100 text-emerald-600' : 'bg-brand-blue/10 text-brand-blue animate-pulse'}`}>
               {testStage === 1 && 'Preparing test lead'}
               {testStage === 2 && 'Sending to gateway'}
               {testStage === 3 && 'Waiting for backend'}
               {testStage === 4 && 'Checking notifications'}
               {testStage === 5 && 'Test complete'}
             </span>
          </div>
          
          {testResult && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Lead Created', val: testResult.success ? 'Yes' : 'No', color: 'text-emerald-500' },
                { label: 'Lead ID', val: testResult.lead_id?.slice(0,8) || 'N/A' },
                { label: 'Notif Status', val: testResult.notification_status || 'N/A', color: testResult.notification_status?.includes('failed') ? 'text-red-500' : 'text-slate-900' },
                { label: 'Service', val: testResult.service_status || 'N/A' }
              ].map(r => (
                <div key={r.label} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase mb-1">{r.label}</p>
                  <p className={`text-xs font-bold truncate ${r.color || 'text-slate-900'}`}>{r.val}</p>
                </div>
              ))}
            </div>
          )}

          {testStage === 5 && (
            <div className="space-y-4">
              <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50">
                <p className="text-[10px] font-bold text-slate-600 flex items-center gap-2">
                  <SafeIcon icon={FiInfo} className="text-brand-blue" />
                  Next checks: 1. Check Leads tab • 2. Check Logs tab • 3. Check recipient inbox • 4. Check spam/junk folder
                </p>
              </div>
              <div className="text-[10px] text-slate-400 font-medium leading-relaxed italic border-t border-slate-50 pt-4">
                Troubleshooting: If the lead row is created but email is not received, check the Google Sheet Logs tab and confirm EmailIt Script Properties are configured.
              </div>
            </div>
          )}
        </div>
      )}

      {Object.keys(validationErrors).length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-[2rem] p-8 animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <SafeIcon icon={FiAlertCircle} className="text-xl" />
            <h4 className="font-black uppercase tracking-widest text-xs">Required Fields Missing</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Object.entries(validationErrors).map(([group, errors]) => errors.length > 0 && (
              <div key={group} className="space-y-2">
                <p className="text-[10px] font-black text-red-400 uppercase tracking-widest">{group} Section</p>
                <ul className="space-y-1">
                  {errors.map((err, idx) => (
                    <li key={idx} className="text-xs text-red-600 font-bold flex items-center gap-2">
                      <div className="w-1 h-1 bg-red-400 rounded-full" /> {err}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-full -mr-16 -mt-16" />
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-brand-blue/5 rounded-xl flex items-center justify-center text-brand-blue"><SafeIcon icon={FiGlobe} /></div>
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Business Profile</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Client Full Name <span className="text-red-400">*</span></label>
                <input required type="text" value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} placeholder="e.g. John Smith" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Business Identity <span className="text-red-400">*</span></label>
                <input required type="text" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="e.g. Smith & Co Legal" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Primary Domain <span className="text-red-400">*</span></label>
                <input required type="text" value={formData.website_domain} onChange={e => setFormData({...formData, website_domain: e.target.value})} placeholder="e.g. smithlegal.com.au" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Plan Level</label>
                <select value={formData.plan} onChange={e => setFormData({...formData, plan: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white appearance-none cursor-pointer">
                  <option>Standard</option><option>Premium</option><option>Enterprise</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-emerald-500/5 rounded-xl flex items-center justify-center text-emerald-500"><SafeIcon icon={FiMail} /></div>
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Notification Engine</h3>
            </div>
            <div className="space-y-10">
              <div className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-widest text-navy-900">Business Alerts</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Send lead notifications to the client</p>
                </div>
                <input type="checkbox" checked={formData.send_business_notification} onChange={e => setFormData({...formData, send_business_notification: e.target.checked})} className="w-6 h-6 rounded-lg text-brand-blue border-slate-300 focus:ring-brand-blue" />
              </div>
              {formData.send_business_notification && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 animate-in fade-in slide-in-from-top-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Recipient Email <span className="text-red-400">*</span></label>
                    <input type="email" value={formData.business_notification_email} onChange={e => setFormData({...formData, business_notification_email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">CC Email</label>
                    <input type="email" value={formData.cc_email} onChange={e => setFormData({...formData, cc_email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                  </div>
                </div>
              )}
              <hr className="border-slate-50" />
              <div className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-widest text-navy-900">Customer Acknowledgement</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Send auto-reply message to visitor</p>
                </div>
                <input type="checkbox" checked={formData.send_customer_confirmation} onChange={e => setFormData({...formData, send_customer_confirmation: e.target.checked})} className="w-6 h-6 rounded-lg text-brand-blue border-slate-300 focus:ring-brand-blue" />
              </div>
              {formData.send_customer_confirmation && (
                <div className="space-y-6 animate-in fade-in slide-in-from-top-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Reply-to Address <span className="text-red-400">*</span></label>
                      <input type="email" value={formData.reply_to_email} onChange={e => setFormData({...formData, reply_to_email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Sender Name</label>
                      <input type="text" value={formData.customer_ack_from_name} onChange={e => setFormData({...formData, customer_ack_from_name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-8">
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-500/5 rounded-xl flex items-center justify-center text-amber-500"><SafeIcon icon={FiCalendar} /></div>
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Service Lifecycle</h3>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Start Date</label>
                <input type="date" value={formData.service_start_date} onChange={e => setFormData({...formData, service_start_date: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">End Date {formData.status === 'Trial' && <span className="text-red-400">*</span>}</label>
                <input type="date" value={formData.service_end_date} onChange={e => setFormData({...formData, service_end_date: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none" />
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Service Status</span>
                <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="bg-slate-100 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest outline-none appearance-none cursor-pointer">
                  <option>Trial</option><option>Active</option><option>Grace</option><option>Expired</option><option>Paused</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-brand-cyan/5 rounded-xl flex items-center justify-center text-brand-cyan"><SafeIcon icon={FiLock} /></div>
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Lead Portal Access</h3>
            </div>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-900">Enable Portal</p>
                  <p className="text-[9px] text-slate-400 font-medium">Allow read-only client login</p>
                </div>
                <input type="checkbox" checked={formData.client_portal_enabled} onChange={e => setFormData({...formData, client_portal_enabled: e.target.checked})} className="w-5 h-5 rounded text-brand-blue border-slate-300" />
              </div>
              {formData.client_portal_enabled && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Portal Login Whitelist <span className="text-red-400">*</span></label>
                    <input type="text" value={formData.client_login_emails} onChange={e => setFormData({...formData, client_login_emails: e.target.value})} placeholder="comma-separated emails" className="w-full px-4 py-3 bg-slate-50 rounded-xl text-[11px] font-bold outline-none" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Client Portal Passcode (6 digits) <span className="text-red-400">*</span></label>
                    <input type="text" value={formData.client_login_code} onChange={e => setFormData({...formData, client_login_code: e.target.value.replace(/\D/g, '').slice(0, 6)})} placeholder="••••••" className="w-full px-4 py-3 bg-slate-50 rounded-xl text-lg font-mono font-black tracking-[0.5em] outline-none" />
                    <p className="text-[9px] text-slate-400 font-bold leading-tight italic ml-1">
                      This is the code the client uses to log into their read-only portal.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-navy-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/10 rounded-full blur-3xl -mr-16 -mt-16" />
            <h3 className="text-lg font-black mb-6 uppercase tracking-tight relative z-10">Integration Script</h3>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 relative z-10 min-h-[100px] flex items-center justify-center">
              {scriptInfo.code ? (
                <code className="text-[10px] text-brand-cyan break-all font-mono leading-relaxed select-all">
                  {scriptInfo.code}
                </code>
              ) : (
                <div className="flex gap-3 text-red-300 text-[10px] font-medium leading-relaxed italic text-center">
                  <p>{!scriptInfo.isSaved ? 'Save client first to generate script.' : 'Public lead.js URL is missing.'}</p>
                </div>
              )}
            </div>
            <button 
              type="button" 
              onClick={async () => {
                if (!scriptInfo.code) return;
                await navigator.clipboard.writeText(scriptInfo.code);
                setCopied(true);
                setNotification({ message: 'Install script copied.', type: 'success' });
                setTimeout(() => setCopied(false), 2000);
              }}
              disabled={!scriptInfo.code}
              className="w-full mt-6 py-4 bg-white/10 hover:bg-white/20 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-30 group"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              {copied ? 'Copied' : 'Copy Install Script'}
            </button>
          </div>

          <button type="submit" disabled={saving} className="w-full py-6 bg-brand-blue text-white rounded-[2rem] font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-brand-blue/30 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50">
            {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiSave} /> {clientKey ? 'Update Configuration' : 'Save & Provision Client'}</>}
          </button>
        </div>
      </form>
    </div>
  );
}